/**
 * Šprtec 3D — herní pravidla nad fyzikou.
 * Jedna instance = jeden zápas. Používá server (online) i klient (single, duel).
 *
 * Fáze: 'aim' (čeká se na tah) → 'sim' (tělesa v pohybu) → zpět na 'aim',
 * případně 'goal' (pauza po gólu) a 'over' (konec zápasu).
 */

import {
  makeBodies,
  resetPositions,
  stepPhysics,
  allStopped,
  stopAll,
  PHYS_DT,
  SIM_MAX_TIME,
  MAX_SHOT_SPEED,
  MIN_SHOT_SPEED,
  RINK,
  PUCK_ID,
} from './physics.js';

export const SCORE_LIMIT = 5;
export const GOAL_PAUSE = 2.6; // s — oslava gólu před rozehrávkou

const L2 = RINK.L / 2;
const GW2 = RINK.GW / 2;

export class Game {
  constructor({ scoreLimit = SCORE_LIMIT, startingSide = null } = {}) {
    this.scoreLimit = scoreLimit;
    this.bodies = makeBodies();
    this.score = [0, 0];
    this.turn = startingSide ?? (Math.random() < 0.5 ? 0 : 1);
    this.phase = 'aim';
    this.simTime = 0;
    this.goalTimer = 0;
    this.pendingKickoff = null;
    this.finished = false;
    this.winner = null;
    this.acc = 0;
  }

  body(id) {
    return this.bodies.find((b) => b.id === id);
  }

  get puck() {
    return this.body(PUCK_ID);
  }

  /**
   * Provede tah: kámen `pieceId` strany `side` dostane rychlost (vx, vz).
   * Vrací true, pokud byl tah přijat.
   */
  applyShot(side, pieceId, vx, vz) {
    if (this.phase !== 'aim' || side !== this.turn) return false;
    const piece = this.body(pieceId);
    if (!piece || piece.kind !== 'piece' || piece.side !== side) return false;
    if (!Number.isFinite(vx) || !Number.isFinite(vz)) return false;

    let sp = Math.hypot(vx, vz);
    if (sp < MIN_SHOT_SPEED) return false;
    if (sp > MAX_SHOT_SPEED) {
      vx *= MAX_SHOT_SPEED / sp;
      vz *= MAX_SHOT_SPEED / sp;
    }

    piece.vx = vx;
    piece.vz = vz;
    this.phase = 'sim';
    this.simTime = 0;
    this.acc = 0;
    return true;
  }

  /** Přepne tah na druhou stranu (např. po vypršení času). */
  forceTurnSwitch() {
    if (this.phase !== 'aim') return;
    this.turn = 1 - this.turn;
  }

  /**
   * Posune hru o dt sekund reálného času. Vrací pole událostí:
   * hit/wall (nárazy), goal, simEnd, turn, reset, over.
   */
  update(dt, events = []) {
    if (this.phase === 'sim') {
      this.acc += Math.min(dt, 0.25);
      while (this.acc >= PHYS_DT && this.phase === 'sim') {
        this.acc -= PHYS_DT;
        stepPhysics(this.bodies, PHYS_DT, events);
        this.simTime += PHYS_DT;
        this.checkGoal(events);
      }
      if (this.phase === 'sim' && (allStopped(this.bodies) || this.simTime > SIM_MAX_TIME)) {
        this.endSim(events);
      }
    } else if (this.phase === 'goal') {
      this.goalTimer -= dt;
      if (this.goalTimer <= 0) {
        if (this.finished) {
          this.phase = 'over';
          events.push({ t: 'over', winner: this.winner, score: [...this.score] });
        } else {
          resetPositions(this.bodies);
          this.turn = this.pendingKickoff;
          this.phase = 'aim';
          events.push({ t: 'reset' });
          events.push({ t: 'turn', side: this.turn });
        }
      }
    }
    return events;
  }

  checkGoal(events) {
    const p = this.puck;
    if (Math.abs(p.x) >= GW2) return;
    if (Math.abs(p.z) < L2 + p.r * 0.6) return;

    // strana 0 útočí na +z
    const scorer = p.z > 0 ? 0 : 1;
    this.score[scorer]++;
    stopAll(this.bodies);
    this.phase = 'goal';
    this.goalTimer = GOAL_PAUSE;
    this.pendingKickoff = 1 - scorer;
    if (this.score[scorer] >= this.scoreLimit) {
      this.finished = true;
      this.winner = scorer;
    }
    events.push({ t: 'goal', scorer, score: [...this.score], final: this.finished });
  }

  endSim(events) {
    stopAll(this.bodies);
    this.returnStrayPieces();
    this.phase = 'aim';
    this.turn = 1 - this.turn;
    events.push({ t: 'simEnd' });
    events.push({ t: 'turn', side: this.turn });
  }

  /** Kameny, které skončily v brankovišti, vrátí na výchozí místo. */
  returnStrayPieces() {
    for (const b of this.bodies) {
      if (b.kind !== 'piece') continue;
      if (Math.abs(b.z) <= L2 - b.r * 0.5) continue;
      b.x = b.home.x;
      b.z = b.home.z;
      // kdyby bylo domovské místo obsazené, posuneme kámen stranou
      let tries = 0;
      while (tries < 20 && this.bodies.some(
        (o) => o !== b && Math.hypot(o.x - b.x, o.z - b.z) < o.r + b.r + 0.01,
      )) {
        b.x += (tries % 2 === 0 ? 1 : -1) * (0.12 + 0.02 * tries);
        tries++;
      }
    }
  }

  /** Kompaktní stav pro přenos po síti. */
  snapshot() {
    return {
      bodies: this.bodies.map((b) => [
        b.id,
        Math.round(b.x * 1000) / 1000,
        Math.round(b.z * 1000) / 1000,
      ]),
      phase: this.phase,
      turn: this.turn,
      score: [...this.score],
    };
  }
}
